<template>
  <!-- ТЕСТОВАЯ СТРАНИЦА нового дизайна брендов (ТЗ 12.10.2026). Адрес /preview/brands, закрыта от Google.
       В меню не стоит. После утверждения блоки переносятся на главную и на /manufacturers. -->
  <div class="pv-banner">
    <div class="container">Test page — new Manufacturers design. Not visible in the site menu.</div>
  </div>

  <!-- 1. Блок на главной -->
  <section class="nb-home">
    <div class="container">
      <div class="nb-home__wrap">
        <div class="nb-home__info">
          <div class="nb-home__label"><block-line></block-line><span>{{ home?.brandsTitle || 'Manufacturers' }}</span></div>
          <h2 class="nb-home__title">{{ home?.brandsSubtitle || 'We stock thousands of automation and control components from leading OEMs' }}</h2>
          <p class="nb-home__text">{{ home?.brandsText }}</p>
          <router-link to="/preview/brands#directory" class="nb-link" @click.prevent="scrollToDirectory">
            Explore all manufacturers <span class="nb-link__arrow">→</span>
          </router-link>
        </div>

        <div class="nb-home__grid">
          <router-link
              v-for="b in featured"
              :key="b.id"
              :to="{ name: 'manufacturer', params: { manufacturer: b.slug } }"
              class="nb-card nb-card--home"
          >
            <span class="nb-card__name" v-html="brandName(b.name)"></span>
            <span v-if="b.subtitle" class="nb-card__sub">{{ b.subtitle }}</span>
            <span class="nb-card__btn" aria-hidden="true">→</span>
            <img v-if="b.card_image" class="nb-card__img" :src="b.card_image" alt="" loading="lazy"/>
          </router-link>

          <a href="#directory" class="nb-card nb-card--home nb-card--all" @click.prevent="scrollToDirectory">
            <span class="nb-card__corner" aria-hidden="true">↗</span>
            <span class="nb-card__all-title">View all manufacturers</span>
            <span class="nb-card__sub">Explore our complete range of brands</span>
          </a>
        </div>
      </div>
    </div>
  </section>

  <!-- 2. Страница всех брендов -->
  <section id="directory" class="nb-dir">
    <div class="container">
      <div class="nb-dir__head">
        <div>
          <h1 class="nb-dir__title">Manufacturers</h1>
          <p class="nb-dir__lead">Leading brands in industrial automation. Thousands of components available from stock or through our global sourcing network.</p>
        </div>
        <label class="nb-search">
          <span class="nb-search__icon" aria-hidden="true">⌕</span>
          <input v-model="search" type="search" placeholder="Search manufacturer…" aria-label="Search manufacturer"/>
        </label>
      </div>

      <div class="nb-az" role="tablist">
        <button
            v-for="l in letters"
            :key="l.key"
            type="button"
            :class="['nb-az__btn', { 'nb-az__btn--active': letter === l.key }]"
            :disabled="!l.count"
            @click="letter = l.key"
        >{{ l.label }}</button>
      </div>

      <p class="nb-dir__count">{{ filtered.length }} {{ filtered.length === 1 ? 'manufacturer' : 'manufacturers' }}</p>

      <div v-if="filtered.length" class="nb-dir__grid">
        <router-link
            v-for="b in filtered"
            :key="b.id"
            :to="{ name: 'manufacturer', params: { manufacturer: b.slug } }"
            class="nb-card nb-card--dir"
        >
          <span class="nb-card__row">
            <span class="nb-card__name nb-card__name--dir" v-html="brandName(b.name)"></span>
            <span class="nb-card__arrow" aria-hidden="true">→</span>
          </span>
          <span v-if="b.subtitle" class="nb-card__sub">{{ b.subtitle }}</span>
          <img v-if="b.card_image" class="nb-card__img nb-card__img--dir" :src="b.card_image" alt="" loading="lazy"/>
        </router-link>
      </div>
      <div v-else class="nb-empty">
        No manufacturers found. Try another spelling — or <router-link to="/contacts">ask us</router-link>, we will source it for you.
      </div>

      <p v-if="note" class="nb-note">{{ note }}</p>
    </div>
  </section>
</template>

<script>
import axios from "axios";
import BlockLine from "@/components/ui/BlockLine.vue";
import {useSiteSettings} from "@/functions/siteSettings";
import {getManufacturers} from "@/functions/dataload";

/** Бренды для главной по ТЗ (порядок важен). */
const FEATURED = ["siemens", "omron", "abb", "phoenix-contact", "pepperlfuchs", "schneider-electric", "sick"];
const AZ = "ABCDEFGHIJKLMNOPQRSTUVWXYZ".split("");

export default {
  components: { BlockLine },
  setup() {
    return { home: useSiteSettings("home"), catalog: useSiteSettings("catalog") };
  },
  data() {
    return { featured: [], all: [], search: "", letter: "all", searchDebounced: "", timer: null };
  },
  computed: {
    note() {
      return (this.catalog?.brandsPageNote ?? "").trim();
    },
    letters() {
      const counts = {};
      for (const b of this.all) {
        const k = this.letterOf(b.name);
        counts[k] = (counts[k] || 0) + 1;
      }
      return [
        { key: "all", label: "All", count: this.all.length },
        ...AZ.map((l) => ({ key: l, label: l, count: counts[l] || 0 })),
        { key: "#", label: "#", count: counts["#"] || 0 },
      ];
    },
    filtered() {
      const q = this.searchDebounced.trim().toLowerCase().replace(/[^a-z0-9]/g, "");
      return this.all.filter((b) => {
        if (this.letter !== "all" && this.letterOf(b.name) !== this.letter) return false;
        if (!q) return true;
        return b.name.toLowerCase().replace(/[^a-z0-9]/g, "").includes(q);
      });
    },
  },
  watch: {
    search(v) {
      clearTimeout(this.timer);
      this.timer = setTimeout(() => {
        this.searchDebounced = v;
        if (v) this.letter = "all";
      }, 200);
    },
  },
  async mounted() {
    const [home, all] = await Promise.all([
      axios.get("/api/manufacturers/home", { params: { slugs: FEATURED.join(",") } }).then((r) => r.data.data).catch(() => []),
      getManufacturers().catch(() => []),
    ]);
    this.featured = home;
    this.all = [...all].sort((a, b) => a.name.localeCompare(b.name, "en", { sensitivity: "base" }));
  },
  methods: {
    letterOf(name) {
      const c = String(name ?? "").trim().charAt(0).toUpperCase();
      return AZ.includes(c) ? c : "#";
    },
    /** Название бренда заглавными; после «+» разрешаем перенос строки. */
    brandName(name) {
      const esc = String(name ?? "").toUpperCase()
          .replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
      return esc.replace(/\+/g, "+<wbr>");
    },
    scrollToDirectory() {
      document.getElementById("directory")?.scrollIntoView({ behavior: "smooth" });
    },
  },
};
</script>

<style scoped>
/* Цвета сайта E-Trade: синий #6083c0, зелёный #7bcfb7, текст #272930, рамки #f4f4f5 */
.pv-banner {
  background: #7bcfb7;
  color: #fff;
  font-family: "Circe-Bold", sans-serif;
  font-size: 13px;
  padding: 6px 0;
  text-align: center;
}

/* ---------- 1. Блок на главной ---------- */
.nb-home {
  padding: 70px 0;
}
.nb-home__wrap {
  display: grid;
  grid-template-columns: minmax(0, 0.8fr) minmax(0, 2fr);
  gap: 48px;
  align-items: center;
}
.nb-home__label {
  display: flex;
  align-items: center;
  font-family: "Circe-Bold", sans-serif;
  font-size: 16px;
  margin-bottom: 22px;
}
.nb-home__title {
  font-size: 34px;
  line-height: 1.12;
  margin-bottom: 22px;
}
.nb-home__text {
  font-size: 16px;
  line-height: 1.55;
  color: rgba(39, 41, 48, 0.55);
  margin-bottom: 26px;
}
.nb-link {
  display: inline-flex;
  align-items: center;
  gap: 14px;
  font-family: "Circe-Bold", sans-serif;
  font-size: 16px;
  color: #6083c0;
  padding-bottom: 6px;
  border-bottom: 2px solid #7bcfb7;
}
.nb-link__arrow {
  transition: transform 0.18s ease;
}
.nb-link:hover .nb-link__arrow {
  transform: translateX(4px);
}
.nb-home__grid {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 14px;
}

/* ---------- Карточка ---------- */
.nb-card {
  position: relative;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  background: #fff;
  border: 1px solid #ebedf2;
  color: #272930;
  transition: border-color 0.18s ease, box-shadow 0.18s ease;
}
.nb-card:hover {
  border-color: #6083c0;
  box-shadow: 0 6px 24px rgba(16, 25, 57, 0.06);
}
.nb-card--home {
  min-height: 200px;
  padding: 24px 22px;
}
.nb-card__name {
  position: relative;
  z-index: 1;
  font-family: "Circe-Bold", sans-serif;
  font-size: 19px;
  line-height: 1.2;
  letter-spacing: 0.06em;
  color: #272930;
}
.nb-card__sub {
  position: relative;
  z-index: 1;
  margin-top: 8px;
  max-width: 70%;
  font-size: 14px;
  line-height: 1.35;
  color: rgba(39, 41, 48, 0.5);
}
.nb-card__btn {
  position: relative;
  z-index: 1;
  margin-top: auto;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  border: 1px solid #dfe5f1;
  background: #fff;
  color: #6083c0;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 18px;
  transition: transform 0.18s ease, border-color 0.18s ease;
}
.nb-card:hover .nb-card__btn {
  transform: translateX(4px);
  border-color: #6083c0;
}
.nb-card__img {
  position: absolute;
  right: -8%;
  bottom: -6%;
  width: 62%;
  height: 72%;
  object-fit: contain;
  object-position: right bottom;
  filter: grayscale(1) blur(3px);
  opacity: 0.26;
  pointer-events: none;
  transition: opacity 0.18s ease, filter 0.18s ease;
  -webkit-mask-image: linear-gradient(to left, #000 55%, transparent);
  mask-image: linear-gradient(to left, #000 55%, transparent);
}
.nb-card:hover .nb-card__img {
  opacity: 0.34;
  filter: grayscale(1) blur(2px);
}
.nb-card--all {
  background: rgba(96, 131, 192, 0.06);
  border-color: rgba(96, 131, 192, 0.45);
}
.nb-card__corner {
  font-size: 26px;
  color: #6083c0;
  margin-bottom: auto;
}
.nb-card__all-title {
  font-family: "Circe-Bold", sans-serif;
  font-size: 19px;
  line-height: 1.2;
  margin-top: 18px;
}

/* ---------- 2. Все бренды ---------- */
.nb-dir {
  padding: 40px 0 80px;
  border-top: 1px solid #f4f4f5;
}
.nb-dir__head {
  display: flex;
  justify-content: space-between;
  align-items: flex-end;
  gap: 30px;
  flex-wrap: wrap;
  margin-bottom: 28px;
}
.nb-dir__title {
  font-size: 46px;
  line-height: 1;
  margin-bottom: 14px;
}
.nb-dir__lead {
  max-width: 640px;
  font-size: 16px;
  line-height: 1.5;
  color: rgba(39, 41, 48, 0.55);
}
.nb-search {
  position: relative;
  width: 100%;
  max-width: 420px;
}
.nb-search__icon {
  position: absolute;
  left: 16px;
  top: 50%;
  transform: translateY(-50%);
  font-size: 20px;
  color: rgba(39, 41, 48, 0.45);
}
.nb-search input {
  width: 100%;
  height: 52px;
  padding: 0 16px 0 46px;
  border: 1px solid #ebedf2;
  font-size: 16px;
  background: #fff;
}
.nb-search input:focus {
  outline: none;
  border-color: #6083c0;
}
.nb-az {
  display: flex;
  flex-wrap: wrap;
  gap: 2px 4px;
  border-bottom: 1px solid #ebedf2;
  margin-bottom: 18px;
}
.nb-az__btn {
  padding: 10px 9px;
  background: none;
  border: none;
  border-bottom: 2px solid transparent;
  font-family: "Circe", sans-serif;
  font-size: 15px;
  color: rgba(39, 41, 48, 0.7);
  cursor: pointer;
  margin-bottom: -1px;
}
.nb-az__btn:hover:not(:disabled) {
  color: #6083c0;
}
.nb-az__btn--active {
  color: #6083c0;
  border-bottom-color: #6083c0;
  font-family: "Circe-Bold", sans-serif;
}
.nb-az__btn:disabled {
  opacity: 0.3;
  cursor: default;
}
.nb-dir__count {
  font-size: 14px;
  color: rgba(39, 41, 48, 0.45);
  margin-bottom: 14px;
}
.nb-dir__grid {
  display: grid;
  grid-template-columns: repeat(6, minmax(0, 1fr));
  gap: 12px;
}
.nb-card--dir {
  min-height: 150px;
  padding: 18px 16px;
}
.nb-card__row {
  position: relative;
  z-index: 1;
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 8px;
}
.nb-card__name--dir {
  font-size: 15px;
  letter-spacing: 0.05em;
  overflow-wrap: anywhere;
}
.nb-card__arrow {
  color: #6083c0;
  font-size: 16px;
  flex: none;
  transition: transform 0.18s ease;
}
.nb-card:hover .nb-card__arrow {
  transform: translateX(4px);
}
.nb-card__img--dir {
  right: 50%;
  transform: translateX(50%);
  bottom: -4%;
  width: 78%;
  height: 62%;
  object-position: center bottom;
  -webkit-mask-image: linear-gradient(to top, #000 50%, transparent);
  mask-image: linear-gradient(to top, #000 50%, transparent);
}
.nb-empty {
  padding: 40px 0;
  font-size: 16px;
  color: rgba(39, 41, 48, 0.6);
}
.nb-empty a {
  color: #6083c0;
  text-decoration: underline;
}
.nb-note {
  margin-top: 26px;
  font-size: 13px;
  line-height: 1.5;
  color: rgba(39, 41, 48, 0.45);
}

/* ---------- Адаптив ---------- */
@media (max-width: 1400px) {
  .nb-home__grid {
    gap: 10px;
  }
  .nb-card--home {
    padding: 20px 18px;
  }
  .nb-card__name {
    font-size: 17px;
  }
}
@media (max-width: 1199px) {
  .nb-home__wrap {
    grid-template-columns: 1fr;
    gap: 30px;
  }
  .nb-dir__grid {
    grid-template-columns: repeat(4, minmax(0, 1fr));
  }
}
@media (max-width: 767px) {
  .nb-home {
    padding: 40px 0;
  }
  .nb-home__title {
    font-size: 26px;
  }
  .nb-home__grid,
  .nb-dir__grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 8px;
  }
  .nb-card--home {
    min-height: 150px;
    padding: 16px 14px;
  }
  .nb-card__name {
    font-size: 14px;
    letter-spacing: 0.04em;
  }
  .nb-card__sub {
    font-size: 12px;
    max-width: 100%;
  }
  .nb-card__btn {
    width: 32px;
    height: 32px;
    font-size: 15px;
  }
  .nb-card__all-title {
    font-size: 15px;
  }
  .nb-dir__title {
    font-size: 32px;
  }
  .nb-card--dir {
    min-height: 110px;
    padding: 14px 12px;
  }
  .nb-card__name--dir {
    font-size: 12px;
  }
}
</style>
